import { css } from "@emotion/react";
import DeleteIcon from "@mui/icons-material/DeleteTwoTone";
import { hexToHsva, hsvaToHex } from "@uiw/color-convert";
import Hue from "@uiw/react-color-hue";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { trpc } from "../../../utils/trpc";
import { PrimaryButton, TertiaryButton } from "../buttons";
import { Input } from "../inputs";
import { FlexColContainer, FlexRowContainer } from "../layout";
import { Modal } from "../Modal";
import { BookmarkCollectionNotebookImage } from "./BookmarkCollectionNotebookImage";

interface CreateOrEditCollectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  renderButton: () => React.ReactNode;
  isEdit?: boolean;
  collection?: { id: number; name: string; colour: string };
}

const initialHsva = { h: 1, s: 64.9, v: 44.7, a: 1 };

const HueBackgroundColour = css`
  background: linear-gradient(
    to right,
    ${hsvaToHex({ h: 0, s: 64.9, v: 70, a: 1 })},
    ${hsvaToHex({ h: 70, s: 64.9, v: 70, a: 1 })} 17%,
    ${hsvaToHex({ h: 120, s: 64.9, v: 70, a: 1 })} 33%,
    ${hsvaToHex({ h: 180, s: 64.9, v: 70, a: 1 })} 50%,
    ${hsvaToHex({ h: 240, s: 64.9, v: 70, a: 1 })} 67%,
    ${hsvaToHex({ h: 300, s: 64.9, v: 70, a: 1 })} 83%,
    ${hsvaToHex({ h: 0, s: 64.9, v: 70, a: 1 })} 100%
  ) !important;
`;

const COLLECTION_NAME_MAX_LENGTH = 50;

export const CreateOrEditCollectionModal = ({
  isOpen,
  onClose,
  renderButton,
  isEdit = false,
  collection,
}: CreateOrEditCollectionModalProps) => {
  const [newCollectionName, setNewCollectionName] = useState("");
  const [hsva, setHsva] = useState(initialHsva);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const navigate = useNavigate();
  const utils = trpc.useUtils();

  useEffect(() => {
    if (isEdit && collection && isOpen) {
      setNewCollectionName(collection.name);
      setHsva(hexToHsva(collection.colour));
    }
  }, [isEdit, collection, isOpen]);

  const createCollection = trpc.createCollection.useMutation({
    onSuccess: async () => {
      await utils.getCollections.invalidate();
      setNewCollectionName("");
      setHsva(initialHsva);
      onClose();
    },
  });

  const updateCollection = trpc.updateCollection.useMutation({
    onSuccess: async () => {
      await utils.getCollections.invalidate();
      onClose();
    },
  });

  const deleteCollection = trpc.deleteCollection.useMutation({
    onSuccess: async () => {
      await utils.getCollections.invalidate();
      setConfirmDelete(false);
      onClose();
      navigate("/bookmarks");
    },
  });

  const handleDelete = () => {
    if (!collection) return;
    deleteCollection.mutate({ collectionId: collection.id });
  };

  const handleSave = () => {
    const name = newCollectionName.trim();
    if (!name) return;

    if (isEdit && collection) {
      updateCollection.mutate({
        collectionId: collection.id,
        name,
        colour: hsvaToHex(hsva),
      });
    } else {
      createCollection.mutate({
        name,
        colour: hsvaToHex(hsva),
      });
    }
  };

  const handleHueChange = (newHue: number) => {
    const value = Math.min(360, Math.max(0, newHue));
    setHsva((prev) => ({ ...prev, h: value }));
  };

  return (
    <>
      <Modal
        key="CreateOrEditCollectionModal"
        isOpen={isOpen}
        onClose={onClose}
        renderButton={renderButton}
        header={<h2>{isEdit ? "Edit" : "New"} Collection</h2>}
        content={
          <FlexRowContainer
            css={({ breakpoints }) => css`
              gap: 0px;

              @media (max-width: ${breakpoints.values.md}px) {
                flex-direction: column;
              }
            `}
          >
            <FlexColContainer
              css={(theme) => css`
                background-color: ${theme.colours.uIGrey.light._5};
                border-radius: 6px;
                flex-grow: 2;
                gap: 60px;
                padding: 20px;
              `}
            >
              <div style={{ position: "relative" }}>
                <Input
                  css={css`
                    margin-bottom: 6px;
                    width: 100%;
                  `}
                  label="Name"
                  name="name"
                  value={newCollectionName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setNewCollectionName(
                      e.target.value.slice(0, COLLECTION_NAME_MAX_LENGTH),
                    );
                  }}
                  required
                />
                <span
                  css={(theme) => css`
                    bottom: -18px;
                    color: ${theme.colours.uIGrey.mid.mid};
                    font-size: 12px;
                    position: absolute;
                    right: 0;
                  `}
                >
                  {newCollectionName.length}/{COLLECTION_NAME_MAX_LENGTH}
                </span>
              </div>

              <FlexColContainer
                css={css`
                  gap: 12px;
                `}
              >
                <FlexRowContainer>
                  <Hue
                    hue={hsva.h}
                    style={{
                      height: "60px",
                      position: "relative",
                      flexGrow: 2,
                      margin: "6px 0",
                    }}
                    css={css`
                      .w-color-interactive {
                        border: 1px solid transparent;
                        border-radius: 6px;
                      }

                      .w-color-alpha-pointer {
                        background-color: ${hsvaToHex(hsva)};
                        border: 2px double #fff;
                        border-radius: 6px;
                        cursor: pointer;
                        height: 69px;
                        left: -6px;
                        margin-left: -6px;
                        top: -5px;
                        width: 13px;
                      }

                      .w-color-alpha-fill {
                        display: none;
                      }

                      > div:first-child {
                        ${HueBackgroundColour}
                        border-radius: 6px !important;
                      }
                    `}
                    onChange={(newHue) => {
                      handleHueChange(newHue.h);
                    }}
                  />
                  <Input
                    css={css`
                      width: 60px;
                    `}
                    label="Hue"
                    name="hue"
                    value={Math.floor(hsva.h)}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const raw = e.target.value.replace(/\D/g, "");
                      if (raw === "") return;
                      handleHueChange(Number(raw));
                    }}
                  />
                </FlexRowContainer>
                <p>
                  This colour is shown on bookmarks and the collection cover.
                </p>
              </FlexColContainer>
            </FlexColContainer>
            <BookmarkCollectionNotebookImage
              collectionName={newCollectionName}
              colour={hsvaToHex(hsva)}
            />
          </FlexRowContainer>
        }
        footer={
          <FlexRowContainer
            css={css`
              justify-content: space-between;
              width: 100%;
            `}
          >
            {isEdit && collection && (
              <div>
                <button
                  css={(theme) => css`
                    background: none;
                    border: none;
                    color: ${theme.colours.accent.mid.dark10};
                    cursor: pointer;
                    padding: 4px;
                    &:hover {
                      color: #000;
                    }
                  `}
                  onClick={() => setConfirmDelete(true)}
                  type="button"
                  title="Delete collection"
                >
                  <DeleteIcon />
                </button>
              </div>
            )}
            <FlexRowContainer
              css={css`
                margin-left: auto;
              `}
            >
              <TertiaryButton onClick={onClose}>Cancel</TertiaryButton>
              <PrimaryButton
                onClick={handleSave}
                disabled={
                  createCollection.isLoading ||
                  updateCollection.isLoading ||
                  !newCollectionName.trim()
                }
              >
                {isEdit
                  ? updateCollection.isLoading
                    ? "Saving..."
                    : "Save"
                  : createCollection.isLoading
                    ? "Creating..."
                    : "Create"}
              </PrimaryButton>
            </FlexRowContainer>
          </FlexRowContainer>
        }
      />
      {isEdit && (
        <Modal
          isOpen={confirmDelete}
          onClose={() => setConfirmDelete(false)}
          renderButton={() => null}
          header={<h2>Delete Collection</h2>}
          content={
            <p>
              Are you sure you want to delete this collection? This action
              cannot be undone.
            </p>
          }
          footer={
            <FlexRowContainer
              css={css`
                margin-left: auto;
              `}
            >
              <TertiaryButton onClick={() => setConfirmDelete(false)}>
                Cancel
              </TertiaryButton>
              <PrimaryButton
                onClick={handleDelete}
                disabled={deleteCollection.isLoading}
              >
                {deleteCollection.isLoading ? "Deleting..." : "Delete"}
              </PrimaryButton>
            </FlexRowContainer>
          }
        />
      )}
    </>
  );
};
